import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useForm, FormProvider } from 'react-hook-form';
import * as yup from 'yup';
import useMediaQuery from '@mui/material/useMediaQuery';
import { VIEW_TYPE } from '@constants/surveys';
import { alertActions, channelsActions, agentsActions } from '@actions';
import { agentsService } from '@services';
import { handleApiError } from '@helpers/api';
import { useTranslation, useYupValidationResolver } from '@hooks';
import { AgentStatus } from '@constants/agents';
import { fromAgentContactFields, toAgentValues } from './agentContactFields';
import General from './Form/GeneralFields';
import Handoff, { HandoffMap } from './Form/Handoff';
import AutoClose, { AutoCloseEnum } from './Form/AutoClose';
import PulseAIPlayground from './PulseAIPlayground';
import Instructions from './Form/Instructions';
import { Modal, Content, Form } from './styles';

const EditModal = ({ modal, onClose, canWrite, canUsePlayground, selectedAgentRef, onDelete }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const isMobile = useMediaQuery('@media (max-width: 768px)');

    const SurveysSchema = yup.object().shape({
        name: yup.string().required('validation.required'),
        channels: yup.array().min(1, 'validation.required'),
        handoffResponseTime: yup.number().when('handoff', {
            is: handoff => handoff === `${HandoffMap.Enabled}`,
            then: schema => schema.required('validation.required'),
            otherwise: schema => schema.notRequired(),
        }),
        closeTimeout: yup.string().when('autoClose', {
            is: value => value === AutoCloseEnum.Close,
            then: schema =>
                schema.test('closeTimeoutFields', 'validation.required', function () {
                    const { daysClose, hoursClose, minutesClose } = this.parent;
                    return daysClose > 0 || hoursClose > 0 || minutesClose > 0;
                }),
            otherwise: schema => schema.notRequired(),
        }),
        unassignTimeout: yup.string().when('autoClose', {
            is: value => value === AutoCloseEnum.Unassign,
            then: schema =>
                schema.test('unassignTimeoutFields', 'validation.required', function () {
                    const { daysUnassign, hoursUnassign, minutesUnassign } = this.parent;
                    return daysUnassign > 0 || hoursUnassign > 0 || minutesUnassign > 0;
                }),
            otherwise: schema => schema.notRequired(),
        }),
        customInstructions: yup.string().max(1000, 'validation.maxLength'),
    });

    const resolver = useYupValidationResolver(SurveysSchema);

    const {
        getValues,
        setValue,
        control,
        watch,
        trigger,
        clearErrors,
        handleSubmit,
        formState: { isValid, isDirty, isSubmitting },
    } = useForm({
        mode: 'all',
        resolver,
        defaultValues: {
            name: '',
            channels: [],
            status: !!AgentStatus.Disabled,
            emailEnabled: false,
            emailRequired: false,
            phoneEnabled: false,
            phoneRequired: false,
            nameEnabled: false,
            nameRequired: false,
            autoClose: AutoCloseEnum.DoNothing,
            closeTimeout: '',
            unassignTimeout: '',
            daysClose: '00',
            hoursClose: '00',
            minutesClose: '00',
            daysUnassign: '00',
            hoursUnassign: '00',
            minutesUnassign: '00',
            handoff: `${HandoffMap.Enabled}`,
            handoffResponseTime: 0,
            customInstructions: '',
        },
    });

    useEffect(() => {
        if (selectedAgentRef.current && modal.open) {
            setValue('name', selectedAgentRef.current.name, { shouldValidate: true });
            setValue('channels', selectedAgentRef.current?.channels ?? [], { shouldValidate: true });
            setValue('status', !!selectedAgentRef.current.status);
            Object.entries(fromAgentContactFields(selectedAgentRef.current)).forEach(([name, value]) =>
                setValue(name, value),
            );
            setValue('autoClose', `${selectedAgentRef.current.autoClose}`, { shouldValidate: true });
            if (selectedAgentRef.current.closeTimeout) {
                const [daysClose, hoursClose, minutesClose] = selectedAgentRef.current.closeTimeout.split(':');
                setValue('daysClose', daysClose);
                setValue('hoursClose', hoursClose);
                setValue('minutesClose', minutesClose);
            }
            if (selectedAgentRef.current.unassignTimeout) {
                const [daysUnassign, hoursUnassign, minutesUnassign] =
                    selectedAgentRef.current.unassignTimeout.split(':');
                setValue('daysUnassign', daysUnassign);
                setValue('hoursUnassign', hoursUnassign);
                setValue('minutesUnassign', minutesUnassign);
            }
            setValue('handoff', `${selectedAgentRef.current.handoff}`, { shouldValidate: true });
            setValue('handoffResponseTime', selectedAgentRef.current.handoffResponseTime, { shouldValidate: true });
            setValue('customInstructions', selectedAgentRef.current.customInstructions || '');
        }
    }, [selectedAgentRef.current, setValue, modal.open]);

    const isView = modal.type === VIEW_TYPE.VIEW;

    const onSubmit = async () => {
        try {
            const {
                daysClose,
                hoursClose,
                minutesClose,
                daysUnassign,
                hoursUnassign,
                minutesUnassign,
                autoClose,
                ...values
            } = getValues();
            await agentsService.createOrUpdateAgent({
                ...selectedAgentRef.current,
                ...toAgentValues(values),
                status: Number(values.status),
                handoff: +values.handoff,
                autoClose: +autoClose,
                closeTimeout: autoClose === AutoCloseEnum.Close ? `${daysClose}:${hoursClose}:${minutesClose}` : null,
                unassignTimeout:
                    autoClose === AutoCloseEnum.Unassign ? `${daysUnassign}:${hoursUnassign}:${minutesUnassign}` : null,
            });
        } catch (error) {
            handleApiError(error, 'alertMessages.editFail');
            return;
        }

        await dispatch(agentsActions.get());
        await dispatch(channelsActions.get());
        dispatch(alertActions.success(t('alertMessages.editSuccess')));
        onClose();
    };

    return (
        <Modal
            fullScreen
            isOpen={modal.open}
            handleClose={onClose}
            isDirty={isDirty}
            title={selectedAgentRef.current ? t('Agents.editAgent') : t('buttons.addAgent')}
            onPrimaryAction={!canWrite ? null : handleSubmit(onSubmit)}
            primaryActionText={t('buttons.saveChanges')}
            primaryActionDisabled={isSubmitting || !isValid || !isDirty}
            onAdditionalAction={!canWrite || !selectedAgentRef.current ? null : onDelete}
            additionalTextButton={t('buttons.delete')}
        >
            <FormProvider {...{ getValues, setValue, control, watch, trigger, clearErrors }}>
                <Content style={canUsePlayground ? undefined : { gridTemplateColumns: '100%' }}>
                    <Form>
                        <General
                            isView={isView}
                            isEdit={modal.type === VIEW_TYPE.EDIT}
                            selectedAgentRef={selectedAgentRef}
                            canWrite={canWrite}
                        />
                        <AutoClose isView={isView} />
                        <Handoff isView={isView} isMobile={isMobile} />
                        <Instructions isView={isView} />
                    </Form>
                    {canUsePlayground && <PulseAIPlayground selectedAgentRef={selectedAgentRef} />}
                </Content>
            </FormProvider>
        </Modal>
    );
};

export default EditModal;
